import { matchingSites } from '@hbs-us/contracts';
import { Button, useTranslation } from '@hbs-us/ui';
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import type { JobDto } from '../contractsMirror.js';
import { useMergeJobsMutation } from '../useJobQuery.js';

/**
 * The job-merge flow (Admin/Manager — the server re-checks the gate, T-22-privesc). The user picks
 * one or more OTHER jobs to merge INTO this (primary) job; on confirm, the server moves every
 * secondary job's child records into the primary and archives the secondaries (tenant-scoped,
 * SEC-11). The destructive confirm rides the focus-trapped `_money/ConfirmDialog`
 * ("Merge these jobs?"). Every color is a locked token (no hex).
 *
 * LIKELY DUPLICATES SORT TO THE TOP. The reason anybody opens this screen is usually that one site
 * got two jobs — crew punching in at the same driveway and writing the address differently. An
 * unranked list of every job in the tenant buries exactly the rows the user came for, so the
 * candidates are split with `matchingSites` from the contracts package.
 *
 * Matching is on jobName, not a stored address, for the same reason MyClock matches on it: a
 * punch-created job names itself after its address, and an office-named job ("Moneytree - 113
 * Dominic Dr") carries one too, so an address-only rule would be blind to every job typed by hand.
 *
 * It is a SUGGESTION, never a filter. Every other job stays selectable below — a false negative
 * here must never make a job unmergeable.
 */

export function JobMergeFlow({
  primaryJob,
  candidateJobs,
}: {
  primaryJob: JobDto;
  candidateJobs: JobDto[];
}) {
  const { t } = useTranslation();
  const merge = useMergeJobsMutation();

  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [confirming, setConfirming] = useState(false);

  // Only OTHER, non-archived jobs are merge candidates (you cannot merge a job into itself).
  const candidates = candidateJobs.filter((j) => j.id !== primaryJob.id);

  const { likely, others } = useMemo(() => {
    const label = primaryJob.jobName ?? '';
    // An unnamed primary has nothing to match on — everything falls through to the full list.
    const matched = label ? matchingSites(label, candidates, (j) => j.jobName) : [];
    const matchedIds = new Set(matched.map((j) => j.id));
    return { likely: matched, others: candidates.filter((j) => !matchedIds.has(j.id)) };
  }, [primaryJob.jobName, candidates]);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function confirmMerge() {
    setConfirming(false);
    const secondaryIds = Array.from(selected);
    if (secondaryIds.length === 0) return;
    merge.mutate(
      { primaryJobId: primaryJob.id, jobIds: [primaryJob.id, ...secondaryIds] },
      { onSuccess: () => setSelected(new Set()) },
    );
  }

  function renderCandidate(job: JobDto, isLikely: boolean) {
    return (
      <label
        key={job.id}
        className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${
          isLikely ? 'border-primary bg-primary/5' : 'border-border bg-background'
        }`}
      >
        <input
          type="checkbox"
          checked={selected.has(job.id)}
          onChange={() => toggle(job.id)}
          className="size-4 rounded border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <span className="font-medium text-foreground">
          {job.jobName || t('jobs:table.unnamed', { id: job.id })}
        </span>
        {job.jobCode ? (
          <span className="font-mono text-muted-foreground text-xs">{job.jobCode}</span>
        ) : null}
      </label>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-6">
        <h3 className="mb-2 font-semibold text-foreground text-lg">{t('jobs:merge.heading')}</h3>
        <p className="text-muted-foreground text-sm">{t('jobs:merge.noCandidates')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-card p-6">
      <h3 className="mb-1 font-semibold text-foreground text-lg">{t('jobs:merge.heading')}</h3>
      <p className="mb-4 text-muted-foreground text-sm">
        {t('jobs:merge.body', { job: primaryJob.jobName ?? '' })}
      </p>

      <fieldset className="space-y-2">
        <legend className="sr-only">{t('jobs:merge.legend')}</legend>

        {likely.length > 0 ? (
          <>
            <p className="font-medium text-foreground text-xs">
              {t('jobs:merge.likelyHeading', { count: likely.length })}
            </p>
            {likely.map((job) => renderCandidate(job, true))}
            {others.length > 0 ? (
              <p className="pt-2 font-medium text-muted-foreground text-xs">
                {t('jobs:merge.otherHeading')}
              </p>
            ) : null}
          </>
        ) : null}

        {others.map((job) => renderCandidate(job, false))}
      </fieldset>

      <div className="mt-4">
        <Button
          type="button"
          variant="destructive"
          disabled={selected.size === 0 || merge.isPending}
          onClick={() => setConfirming(true)}
        >
          {t('jobs:merge.action', { count: selected.size })}
        </Button>
      </div>

      {merge.isError ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('jobs:merge.error')}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirming}
        heading={t('jobs:merge.confirmHeading')}
        body={t('jobs:merge.confirmBody')}
        confirmLabel={t('jobs:merge.confirmAction')}
        cancelLabel={t('jobs:merge.confirmCancel')}
        confirmPending={merge.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={confirmMerge}
      />
    </div>
  );
}
